import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Users, ClipboardEdit, Plus, ExternalLink } from "lucide-react";

const PROVINCES = ["ON", "QC", "BC", "AB", "MB", "SK", "NS", "NB", "NL", "PE", "NT", "NU", "YT"];
const US_STATES = ["AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY", "DC"];
const DIVISIONS = ["18U", "16U", "15U", "14U", "13U", "12U"];
const GAME_TYPES = [
  ["exhibition", "Exhibition"], ["scrimmage", "Scrimmage"], ["league", "League Game"], ["tournament", "Tournament"],
  ["tournament_playoff", "Tournament Playoff"], ["provincial_championship", "Provincial Championship"],
  ["national_championship", "National Championship"], ["international", "International"],
];
const TOURNAMENT_TYPES = ["tournament", "tournament_playoff", "provincial_championship", "national_championship", "international"];
const inp = "w-full border border-slate-200 rounded-sm px-3 py-2 text-sm";

export default function AdminManualEntry() {
  const [tab, setTab] = useState("results");
  const [teams, setTeams] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const load = () => {
    api.get("/live/teams").then(({ data }) => setTeams(data)).catch(() => {});
    api.get("/live/leagues", { params: { active: true } }).then(({ data }) => setLeagues(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  return (
    <AdminLayout>
    <div className="max-w-3xl mx-auto p-6" data-testid="admin-manual-entry">
      <div className="label-overline text-[#E63946]">CBR Live</div>
      <h1 className="font-display text-3xl uppercase tracking-tight text-[#0A192F]">Manual Data Entry</h1>
      <p className="text-sm text-slate-500 mt-1 mb-5">Add teams &amp; completed results so CBR Team Rankings stay complete before every team adopts CBR Live.</p>

      <div className="flex gap-2 mb-6 border-b border-slate-200">
        <TabBtn active={tab === "results"} onClick={() => setTab("results")} icon={ClipboardEdit} label="Enter Result" testid="tab-results" />
        <TabBtn active={tab === "teams"} onClick={() => setTab("teams")} icon={Users} label="Create Team" testid="tab-teams" />
      </div>

      {tab === "teams"
        ? <TeamTab teams={teams} onSaved={load} />
        : <ResultTab teams={teams} leagues={leagues} onSaved={load} />}
    </div>
    </AdminLayout>
  );
}

const TabBtn = ({ active, onClick, icon: Icon, label, testid }) => (
  <button data-testid={testid} onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold uppercase tracking-wider border-b-2 ${active ? "border-[#E63946] text-[#0A192F]" : "border-transparent text-slate-400"}`}>
    <Icon className="w-4 h-4" /> {label}
  </button>
);

function TeamTab({ teams, onSaved }) {
  const [f, setF] = useState({ name: "", province: "", city_region: "", age_division: "18U" });
  const [busy, setBusy] = useState(false);
  const save = () => {
    if (!f.name.trim()) return toast.error("Team name required");
    setBusy(true);
    api.post("/live/teams", f).then(() => { toast.success("Team created"); setF({ name: "", province: "", city_region: "", age_division: "18U" }); onSaved(); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed")).finally(() => setBusy(false));
  };
  return (
    <div className="space-y-6">
      <div className="bg-white border border-slate-200 rounded-lg p-5 grid sm:grid-cols-2 gap-3" data-testid="manual-team-form">
        <input data-testid="mt-name" placeholder="Team name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={`${inp} sm:col-span-2`} />
        <select data-testid="mt-province" value={f.province} onChange={(e) => setF({ ...f, province: e.target.value })} className={inp}>
          <option value="">Province / State…</option>
          <optgroup label="Canada — Provinces">{PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}</optgroup>
          <optgroup label="USA — States">{US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}</optgroup>
        </select>
        <input placeholder="City / Region" value={f.city_region} onChange={(e) => setF({ ...f, city_region: e.target.value })} className={inp} />
        <select data-testid="mt-division" value={f.age_division} onChange={(e) => setF({ ...f, age_division: e.target.value })} className={inp}>{DIVISIONS.map((d) => <option key={d} value={d}>{d}</option>)}</select>
        <button data-testid="mt-submit" disabled={busy} onClick={save} className="sm:col-span-2 bg-[#E63946] text-white text-sm font-semibold uppercase tracking-wider py-2.5 rounded-sm hover:bg-[#c62f3b] disabled:opacity-50 flex items-center justify-center gap-2"><Plus className="w-4 h-4" /> Create Team</button>
      </div>
      <div>
        <div className="label-overline mb-2">All Teams ({teams.length})</div>
        <div className="grid sm:grid-cols-2 gap-2">
          {teams.map((t) => (
            <Link key={t.id} to={`/live/teams/${t.slug}`} className="flex items-center justify-between bg-white border border-slate-200 rounded-sm px-3 py-2 hover:border-[#E63946]">
              <div className="min-w-0"><div className="text-sm font-semibold truncate">{t.name}</div><div className="text-[11px] text-slate-400">{[t.age_division, t.province].filter(Boolean).join(" · ")}</div></div>
              <ExternalLink className="w-3.5 h-3.5 text-slate-300" />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function ResultTab({ teams, leagues, onSaved }) {
  const [f, setF] = useState({
    date: "", home_team_id: "", away_team_id: "", home_score: 0, away_score: 0,
    home_hits: 0, away_hits: 0, home_errors: 0, away_errors: 0,
    game_type: "league", league_id: "", playoff_round: "final", neutral_site: false, notes: "",
    tournament: { name: "", location: "", organizer: "" },
  });
  const [busy, setBusy] = useState(false);
  const isTourn = TOURNAMENT_TYPES.includes(f.game_type);
  const setT = (k, v) => setF({ ...f, tournament: { ...f.tournament, [k]: v } });
  const save = () => {
    if (!f.date || !f.home_team_id || !f.away_team_id) return toast.error("Date and both teams required");
    if (f.home_team_id === f.away_team_id) return toast.error("Teams must differ");
    if (f.game_type === "league" && !f.league_id) return toast.error("Select a league");
    if (isTourn && !f.tournament.name) return toast.error("Tournament name required");
    const body = { ...f, home_score: Number(f.home_score), away_score: Number(f.away_score),
      home_hits: Number(f.home_hits), away_hits: Number(f.away_hits),
      home_errors: Number(f.home_errors), away_errors: Number(f.away_errors) };
    if (f.game_type !== "league") body.league_id = null;
    if (!isTourn) body.tournament = null;
    setBusy(true);
    api.post("/live/manual-games", body).then(() => { toast.success("Result recorded — rankings updated"); onSaved(); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed")).finally(() => setBusy(false));
  };
  const TeamSel = ({ side, testid }) => (
    <select data-testid={testid} value={f[`${side}_team_id`]} onChange={(e) => setF({ ...f, [`${side}_team_id`]: e.target.value })} className={inp}>
      <option value="">{side === "home" ? "Home team…" : "Away team…"}</option>
      {teams.map((t) => <option key={t.id} value={t.id}>{t.name}{t.age_division ? ` (${t.age_division})` : ""}</option>)}
    </select>
  );
  const Num = ({ side, field, label }) => (
    <div><label className="text-[10px] uppercase tracking-wider text-slate-400">{label}</label>
      <input type="number" min="0" value={f[`${side}_${field}`]} onChange={(e) => setF({ ...f, [`${side}_${field}`]: e.target.value })} className={inp} /></div>
  );
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4" data-testid="manual-result-form">
      <input data-testid="mr-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={inp} />
      <div className="grid grid-cols-2 gap-3"><TeamSel side="home" testid="mr-home-team" /><TeamSel side="away" testid="mr-away-team" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="text-[10px] uppercase tracking-wider text-slate-400">Home Score</label><input data-testid="mr-home-score" type="number" min="0" value={f.home_score} onChange={(e) => setF({ ...f, home_score: e.target.value })} className={inp} /></div>
        <div><label className="text-[10px] uppercase tracking-wider text-slate-400">Away Score</label><input data-testid="mr-away-score" type="number" min="0" value={f.away_score} onChange={(e) => setF({ ...f, away_score: e.target.value })} className={inp} /></div>
        <Num side="home" field="hits" label="Home Hits" /><Num side="away" field="hits" label="Away Hits" />
        <Num side="home" field="errors" label="Home Errors" /><Num side="away" field="errors" label="Away Errors" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <select data-testid="mr-game-type" value={f.game_type} onChange={(e) => setF({ ...f, game_type: e.target.value })} className={inp}>{GAME_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.neutral_site} onChange={(e) => setF({ ...f, neutral_site: e.target.checked })} /> Neutral site</label>
      </div>
      {f.game_type === "league" && (
        <select data-testid="mr-league" value={f.league_id} onChange={(e) => setF({ ...f, league_id: e.target.value })} className={inp}>
          <option value="">Select league…</option>
          {leagues.map((l) => <option key={l.id} value={l.id}>{l.abbreviation ? `${l.abbreviation} — ` : ""}{l.name}</option>)}
        </select>
      )}
      {isTourn && <div className="grid sm:grid-cols-3 gap-3">
        <input data-testid="mr-tournament-name" placeholder="Tournament name" value={f.tournament.name} onChange={(e) => setT("name", e.target.value)} className={inp} />
        <input placeholder="Location" value={f.tournament.location} onChange={(e) => setT("location", e.target.value)} className={inp} />
        <input placeholder="Organizer" value={f.tournament.organizer} onChange={(e) => setT("organizer", e.target.value)} className={inp} />
      </div>}
      {f.game_type === "tournament_playoff" && (
        <select data-testid="mr-playoff-round" value={f.playoff_round} onChange={(e) => setF({ ...f, playoff_round: e.target.value })} className={inp}>
          {["quarterfinal", "semifinal", "final"].map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      )}
      <textarea placeholder="Notes (optional)" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className={inp} rows={2} />
      <button data-testid="mr-submit" disabled={busy} onClick={save} className="w-full bg-[#E63946] text-white text-sm font-semibold uppercase tracking-wider py-2.5 rounded-sm hover:bg-[#c62f3b] disabled:opacity-50">Record Result</button>
    </div>
  );
}
